import React, {useEffect} from 'react';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {useControllerProps} from '../../../../../domain/controllers/ControllerContext';
import {ButtonPriority} from '@wix/wix-ui-tpa';
import {classes} from './Footer.st.css';
import {CheckoutButton} from '../../../../../common/components/CheckoutButton/CheckoutButton';
import {CartButton} from '../CartButton/CartButton';
import {SecureCheckout} from '../../../../../common/components/SecureCheckout/SecureCheckout';
import {Violations} from '../../../../../common/components/Violations/Violations';
import settingsParams from '../../../settingsParams';
import stylesParams from '../../../stylesParams';
import {Divider, Text} from '@wix/wix-ui-tpa/cssVars';
import {ExpressButtons} from '../../../../../common/components/ExpressButtons';
import classNames from 'clsx';
import {useBooleanStyleForAnyBreakpoint} from '../../../../../common/useBooleanStyleForAnyBreakpoint';
import {useSettingsWithDefaults} from '../../../../../hooks/useSettingsWithDefaults';
import {FooterSkeleton} from './FooterSkeleton/FooterSkeleton';
import {SkeletonLineWrapper} from '../../../../../common/components/Skeleton/SkeletonLine/SkeletonLineWrapper';
import {EstimateTotalsErrorNotification} from '../../../../../common/components/EstimateTotalsErrorNotification/EstimateTotalsErrorNotification';
import {SlotPlaceHolder} from '../../../../../common/components/SlotPlaceHolder/SlotPlaceHolder';
import {SlotsMap} from '../../../../../common/constants';

export enum FooterDataHooks {
  root = 'Footer.root',
  container = 'Footer.container',
  skeleton = 'Footer.skeleton',
  estimatedTotalContainer = 'Footer.estimatedTotalContainer',
  estimatedTotalLabel = 'Footer.estimatedTotalLabel',
  estimatedTotalValue = 'Footer.estimatedTotalValue',
  estimatedTotalValueSkeleton = 'Footer.estimatedTotalValueSkeleton',
  estimatedTotalDisclaimer = 'Footer.estimatedTotalDisclaimer',
  currencyConverterDisclaimer = 'Footer.currencyConverterDisclaimer',
  expressCheckoutButtons = 'Footer.expressCheckoutButtons',
  expressButtonsSkeleton = 'Footer.expressButtonsSkeleton',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const Footer = () => {
  const {t} = useTranslation();
  const {
    cartModel,
    cartSummary,
    shouldRenderEmptyState,
    sideCartElementsVisibilityInCss,
    pageLoaded,
    isSummaryUpdating,
    isSummaryLoading,
    errorOnEstimateTotals,
    reportCheckoutButtonPresented,
    reportCartButtonPresented,
  } = useControllerProps().cartStore;
  const {shouldShowExpressCheckout} = useControllerProps().cashierExpressStore;
  const {orderStore} = useControllerProps();
  const {isCheckoutButtonDisabled} = useControllerProps().navigationStore;
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const {getBooleanStyle} = useBooleanStyleForAnyBreakpoint();

  const shouldShowTotalsDisclaimer = getBooleanStyle(
    stylesParams.CART_SUMMARY_DISCLAIMER_VISIBILITY,
    sideCartElementsVisibilityInCss
  );
  const shouldShowSecureCheckout = getBooleanStyle(
    stylesParams.CART_SUMMARY_SECURE_BADGE_VISIBILITY,
    sideCartElementsVisibilityInCss
  );
  const shouldGoToCartButton = getBooleanStyle(
    stylesParams.CART_ACTION_BUTTONS_GO_TO_CART_VISIBILITY,
    sideCartElementsVisibilityInCss
  );
  const shouldShowCheckoutButton = getBooleanStyle(
    stylesParams.CART_ACTION_BUTTONS_CHECKOUT_VISIBILITY,
    sideCartElementsVisibilityInCss
  );

  useEffect(
    () => {
      pageLoaded();
    },
    /* eslint-disable react-hooks/exhaustive-deps*/ []
  );

  useEffect(() => {
    if (!shouldRenderEmptyState) {
      reportCheckoutButtonPresented(shouldShowCheckoutButton);
      reportCartButtonPresented(shouldGoToCartButton);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowCheckoutButton, shouldGoToCartButton]);

  if (shouldRenderEmptyState) {
    return false;
  }

  const {shouldShowConversionDisclaimer} = orderStore;

  const estimatedTotalAmount =
    cartSummary?.priceSummary.total.formattedConvertedAmount ?? cartModel.subtotal?.formattedConvertedAmount;

  const footerContent = () => {
    if (errorOnEstimateTotals) {
      return <EstimateTotalsErrorNotification />;
    }
    if (isSummaryLoading) {
      return <FooterSkeleton />;
    }

    return (
      <div data-hook={FooterDataHooks.container} className={classes.container}>
        <div
          className={classes.estimatedTotalRow}
          aria-live="polite"
          role="status"
          data-hook={FooterDataHooks.estimatedTotalContainer}>
          <Text tagName={'h3'} data-hook={FooterDataHooks.estimatedTotalLabel} className={classes.estimatedTotalLabel}>
            {t('sideCart.estimatedTotal.label')}
          </Text>
          {isSummaryUpdating ? (
            <SkeletonLineWrapper dataHook={FooterDataHooks.estimatedTotalValueSkeleton} size={'medium'} />
          ) : (
            <Text
              data-hook={FooterDataHooks.estimatedTotalValue}
              className={classes.estimatedTotalValue}
              data-wix-sub-totals-value>
              {estimatedTotalAmount}
            </Text>
          )}
        </div>
        <div className={classes.disclaimers}>
          {shouldShowConversionDisclaimer && (
            <Text data-hook={FooterDataHooks.currencyConverterDisclaimer} className={classes.disclaimerText}>
              {t('cart.currency_converter_disclaimer', {mainCurrency: cartModel.businessInfo.currencyCode})}
            </Text>
          )}
          {shouldShowTotalsDisclaimer ? (
            <Text
              data-hook={FooterDataHooks.estimatedTotalDisclaimer}
              className={classNames(
                classes.disclaimerText,
                sideCartElementsVisibilityInCss ? classes.disclaimerTextVisibility : undefined
              )}>
              {getSettingsWithDefaults(settingsParams.CART_DISCLAIMER_TEXT)}
            </Text>
          ) : null}
        </div>
        <Violations />
        <SlotPlaceHolder slotId={SlotsMap.SideCartFooterActionsBefore1} className={classes.footerActionsBefore1Slot} />
        <div className={classes.buttons}>
          {shouldShowCheckoutButton ? (
            <CheckoutButton
              className={sideCartElementsVisibilityInCss ? classes.checkoutButtonVisibility : undefined}
              priority={ButtonPriority.primary}
              disabled={isCheckoutButtonDisabled}
              text={getSettingsWithDefaults(settingsParams.SIDE_CART_CHECKOUT_BUTTON)}
            />
          ) : null}
          {shouldGoToCartButton ? (
            <CartButton className={sideCartElementsVisibilityInCss ? classes.cartButtonVisibility : null} />
          ) : null}
          {shouldShowExpressCheckout ? (
            <div className={classes.expressCheckoutButtonsVisibility}>
              <ExpressButtons dataHook={FooterDataHooks.expressCheckoutButtons} withLoader={true} />
            </div>
          ) : null}
        </div>
        <SlotPlaceHolder slotId={SlotsMap.SideCartFooterActionsAfter1} className={classes.footerActionsAfter1Slot} />
        {shouldShowSecureCheckout ? (
          <SecureCheckout
            text={t('sideCart.secureCheckout.badge')}
            className={sideCartElementsVisibilityInCss ? classes.secureCheckoutVisibility : undefined}
          />
        ) : null}
      </div>
    );
  };

  return (
    <section className={classes.root} data-hook={FooterDataHooks.root}>
      <Divider className={classes.divider} />
      {footerContent()}
    </section>
  );
};
